import { useQuery } from '@tanstack/react-query';

import { RecordsWithTimestamp, SearchHistoryEntry } from '@app/components';
import {
  queryKeys,
  useFormattedSearches,
  usePatronSession,
  UserSearchDto,
} from '@app/hooks';
import { makeRequest } from '@app/utils';

export interface ProjectRecordInstanceDto {
  id: string;
  instanceId: string;
  metadata: { createdDate: string };
}

export interface ProjectDetails {
  description: string;
  id: string;
  name: string;
  projectInstances: ProjectRecordInstanceDto[];
  projectSearches: UserSearchDto[];
  totalRecords: number;
}

export interface ProjectDetailsTransformed
  extends Omit<ProjectDetails, 'projectSearches' | 'projectInstances'> {
  projectSearches: SearchHistoryEntry[];
  projectInstances: RecordsWithTimestamp[];
}

export const useProjectDetails = ({ projectId }: { projectId: string }) => {
  const { externalSystemId } = usePatronSession();

  const url = `/opac-patron/account/${externalSystemId}/projects/${projectId}`;
  const formatSearches = useFormattedSearches();

  return useQuery<ProjectDetails, unknown, ProjectDetailsTransformed>(
    [queryKeys.projectDetails, projectId],
    () =>
      makeRequest({
        url,
        params: {
          includeSearches: true,
          includeInstances: true,
        },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: details => ({
        ...details,
        projectSearches: formatSearches(details.projectSearches),
        projectInstances: details.projectInstances.map(instance => ({
          ...instance,
          timestamp: instance.metadata.createdDate,
        })),
      }),
    }
  );
};
